import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

export type EquipmentCategory =
  | "elevator"
  | "hvac"
  | "plumbing"
  | "electric"
  | "fire"
  | "exterior"
  | "interior"
  | "other";

export type EquipmentStatus = "稼働中" | "廃止";

/**
 * 収益種別 — 設備が収益を生む場合の分類
 */
export type EquipmentRevenueType = "ancillary_income" | "other_income";
export type EquipmentRevenueStatus = "active" | "suspended" | "planned";

export interface EquipmentDocument {
  document_id: string;
  file_name: string;
  file_type: string;
  s3_key: string;
  uploaded_at: string;
}

export interface EquipmentRecord {
  equipment_id: string;
  client_id: string;
  property_id: string;

  name: string;
  category: EquipmentCategory;
  location: string;
  manufacturer?: string;
  model?: string;
  install_date?: string;
  useful_life_years?: number;
  replacement_date?: string;
  replacement_cost?: number;
  status: EquipmentStatus;

  // 収益情報 (機械式駐車場・屋上太陽光等の付帯収益設備で使用)
  is_revenue_generating?: boolean;
  revenue_type?: EquipmentRevenueType;
  revenue_status?: EquipmentRevenueStatus;
  monthly_income?: number;
  contractor_name?: string;
  contract_start_date?: string;
  contract_end_date?: string;

  notes?: string;
  documents?: EquipmentDocument[];

  created_at: string;
  updated_at?: string;
}

interface EquipmentState {
  list: {
    data: EquipmentRecord[];
    loading: boolean;
    error: string | null;
  };
}

const initialState: EquipmentState = {
  list: { data: [], loading: false, error: null },
};

// ── Equipment ──

export const fetchEquipment = createAsyncThunk<
  EquipmentRecord[],
  { property_id: string }
>("equipment/fetch", async ({ property_id }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(`${BaseURL}/api/v1/equipment`, {
      params: { property_id },
    });
    return res.data.data.items;
  } catch {
    return rejectWithValue("設備データの取得に失敗しました");
  }
});

export const fetchEquipmentById = createAsyncThunk<
  EquipmentRecord,
  { equipment_id: string }
>("equipment/fetchById", async ({ equipment_id }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(
      `${BaseURL}/api/v1/equipment/${equipment_id}`,
    );
    return res.data.data;
  } catch {
    return rejectWithValue("設備データの取得に失敗しました");
  }
});

export const createEquipment = createAsyncThunk<
  EquipmentRecord,
  {
    property_id: string;
    name: string;
    category: EquipmentCategory;
    location: string;
    manufacturer?: string;
    model?: string;
    install_date?: string;
    useful_life_years?: number;
    replacement_date?: string;
    replacement_cost?: number;
    status?: EquipmentStatus;
    is_revenue_generating?: boolean;
    revenue_type?: EquipmentRevenueType;
    revenue_status?: EquipmentRevenueStatus;
    monthly_income?: number;
    contractor_name?: string;
    contract_start_date?: string;
    contract_end_date?: string;
    notes?: string;
    documents?: EquipmentDocument[];
  }
>("equipment/create", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(`${BaseURL}/api/v1/equipment`, body);
    return res.data.data;
  } catch {
    return rejectWithValue("設備の登録に失敗しました");
  }
});

export const updateEquipment = createAsyncThunk<
  EquipmentRecord,
  { equipment_id: string; body: Partial<EquipmentRecord> }
>("equipment/update", async ({ equipment_id, body }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.put(
      `${BaseURL}/api/v1/equipment/${equipment_id}`,
      body,
    );
    return res.data.data;
  } catch {
    return rejectWithValue("設備の更新に失敗しました");
  }
});

export const deleteEquipment = createAsyncThunk<string, { equipment_id: string }>(
  "equipment/delete",
  async ({ equipment_id }, { rejectWithValue }) => {
    try {
      await ipponApi.delete(`${BaseURL}/api/v1/equipment/${equipment_id}`);
      return equipment_id;
    } catch {
      return rejectWithValue("設備の削除に失敗しました");
    }
  },
);

const equipmentSlice = createSlice({
  name: "equipment",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEquipment.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchEquipment.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload;
      })
      .addCase(fetchEquipment.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload as string;
      })
      .addCase(createEquipment.fulfilled, (state, action) => {
        state.list.data.unshift(action.payload);
      })
      .addCase(updateEquipment.fulfilled, (state, action) => {
        const idx = state.list.data.findIndex(
          (e) => e.equipment_id === action.payload.equipment_id,
        );
        if (idx >= 0) state.list.data[idx] = action.payload;
      })
      .addCase(deleteEquipment.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter(
          (e) => e.equipment_id !== action.payload,
        );
      });
  },
});

export default equipmentSlice.reducer;
